import { BardCodeType, CardActions, CardActionPayload, CardConfigDataFE, CardStatus, CardType, UserCardFE, UserRecord } from '../../../user.model';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels } from '@techiediaries/ngx-qrcode';
import { animate, style, transition, trigger } from '@angular/animations';
import { filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { CardPrintService } from 'src/app/global/utils/card-print/cart-print.service';
import { ShortDatePipe } from 'src/app/global/pipes/shortDate/short-date.pipe';
import { Subject } from 'rxjs';
import { SubscriptionType, Permissions, AccessLevels } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../../service/user.abstract.service';
import { Organisation } from '../../../../global/global.model';
import { organisationConfigurationSelector, organisationSelector, organisationTagSelector } from 'src/app/global/store/global.selectors';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { userScopesSelector } from 'src/app/user/store/user.selectors';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/global/components/dialogs/confirm-dialog/confirm-dialog.component';

export enum DataState {
    ENTERING = 'ENTERING',
    DONE = 'DONE',
}

export enum CardView {
    DEFAULT = 'DEFAULT',
    SUBSCRIPTIONS = 'SUBSCRIPTIONS',
}

@Component({
    selector: 'app-card',
    templateUrl: './card.component.html',
    styleUrls: ['./card.component.scss'],
    animations: [trigger('dataChange', [transition(`${DataState.DONE} => ${DataState.ENTERING}`, [style({ opacity: '0' }), animate('200ms ease', style({ opacity: '1' }))])])],
})
export class CardComponent implements OnDestroy, OnInit {
    destroy$ = new Subject();

    CardStatus = CardStatus;
    CardType = CardType;
    BardCodeType = BardCodeType;
    DataState = DataState;
    CardView = CardView;
    organisationFeatures: any;
    organisation: Organisation;
    canShowSubscriptions = false;
    canWriteCard = false;

    public qrcodeOpt = {
        elementType: NgxQrcodeElementTypes.URL,
        correctionLevel: NgxQrcodeErrorCorrectionLevels.HIGH,
    };

    public dataState: DataState = DataState.DONE;
    private _card: Partial<UserCardFE>;
    private _orgAndConfig: CardConfigDataFE;
    public _loading: boolean; // hack to fix ngx-qrcode rendering twice on data change between two active cards
    private _cardView: CardView = CardView.DEFAULT;
    public cardPrintLoading: boolean = false;
    private allCoreConfig: any;

    get cardView() {
        return this._cardView;
    }
    set cardView(v: CardView) {
        this._cardView = v;
    }

    @Input() user: UserRecord;

    @Input()
    set card(newCard: Partial<UserCardFE>) {
        this._loading = true;
        this.dataState = DataState.ENTERING;
        this.cardView = CardView.DEFAULT;
        this._card = newCard;
        this.store
            .select(userScopesSelector)
            .pipe(
                filter((scopes) => scopes !== undefined),
                take(1),
            )
            .subscribe((scopes) => {
                if (scopes && newCard?.cardType) {
                    this.canWriteCard = scopes.some(
                        (scope) =>
                            scope.permission === Permissions.CARD &&
                            scope.resource === 'card_type' &&
                            (scope.resourceId === '*' || scope.resourceId === newCard.cardType) &&
                            scope.accessLevel === AccessLevels.WRITE,
                    );
                }
            });
        this._loading = false;
    }
    get card(): Partial<UserCardFE> {
        return this._card;
    }

    @Input()
    set orgAndConfig(config: CardConfigDataFE) {
        this._orgAndConfig = config;
    }
    get orgAndConfig() {
        return this._orgAndConfig;
    }

    @Output() cardActions = new EventEmitter();

    get firstName() {
        return this.card?.firstName;
    }
    get lastName() {
        return this.card?.lastName;
    }
    get photo() {
        return this.card?.photo || 'https://www.pphfoundation.ca/wp-content/uploads/2018/05/default-avatar.png';
    }
    get status() {
        return this.card?.status;
    }
    get code() {
        return this.card?.code;
    }
    get qrCodeForImage() {
        if (this.orgName.toUpperCase() === 'BROSSARD') {
            return `QBD${this.card.code}`;
        }
        return this.card?.code;
    }
    get fomatedPostCode() {
        return this.card?.address?.postCode && this.card?.address?.postCode.length >= 6
            ? `${this.card?.address?.postCode.substring(0, 3)} ${this.card?.address?.postCode.substring(3, this.card?.address?.postCode.length)}`
            : this.card?.address?.postCode;
    }
    get cardType() {
        return this.card?.cardType;
    }
    get subType() {
        return this.card?.subType;
    }
    get expDate() {
        return this.card?.expirationDate;
    }
    get orgName() {
        return this.orgAndConfig?.org?.name;
    }
    get orgLogo() {
        return this.orgAndConfig?.org?.logo;
    }
    get cardConfig() {
        return this.orgAndConfig?.config?.support?.cards?.find((c) => c.type === this.cardType);
    }
    get expDateString() {
        return this.formatDateUTC(this.card?.expirationDate);
    }
    get displayExpDate() {
        return this.cardConfig?.displayExpirationDate ?? true;
    }
    get subscriptions() {
        return this.card?.subscriptions;
    }
    get linkable() {
        return this.cardConfig?.linkable;
    }
    get creatable() {
        return this.cardConfig?.creatable ?? true; // Default to true if not specified
    }
    get subTypeColor() {
        return this.orgAndConfig?.config?.subTypes?.find((c) => c.tag === this.card?.subType)?.color || '';
    }
    get subTypeName() {
        return this.orgAndConfig?.config?.subTypes?.find((c) => c.tag === this.card?.subType)?.name[this.translateService.currentLang] || '';
    }
    get showCardInfo() {
        if (this.code) {
            return true;
        }
        return false;
    }
    get cardLabel() {
        return this.cardConfig?.identity?.label[this.translateService.currentLang] || '';
    }
    get cardIcon() {
        return this.cardConfig?.identity?.icon || 'icon-my-card';
    }
    get instructionsGeneralIntroduction() {
        return this.cardConfig?.identity?.instructions?.general?.introduction[this.translateService.currentLang] || '';
    }
    get subscribeTooltipButton() {
        return this.cardConfig?.tooltips?.subscribe?.[this.translateService.currentLang] || '';
    }
    get linkTooltipButton() {
        return this.cardConfig?.tooltips?.link?.[this.translateService.currentLang] || '';
    }

    get showExpireSoonMessage() {
        if (this.cardType === CardType.LEGACY) {
            return false;
        }
        // Set a threshold warning date by taking expiration date - renewalDelay (ex: 60)
        // Check if today's date with .getTime() is larger than the threshold date.
        // if so, return true
        const today = new Date();
        if (!this?.card?.expirationDate) {
            return false;
        }
        const expirationDate = new Date(this?.card?.expirationDate);
        const warningThresholdDate = expirationDate.setDate(expirationDate.getDate() - this?.cardConfig.renewalDelay);
        return warningThresholdDate < today.getTime();
    }

    get showExpireMessage() {
        // Set a threshold warning date by taking expiration date - renewalDelay (ex: 60)
        // Check if today's date with .getTime() is larger than the threshold date.
        // if so, return true
        const today = new Date();
        const expirationDate = this?.card?.expirationDate ? new Date(this?.card?.expirationDate) : null;
        return expirationDate && expirationDate < today;
    }

    get cardPrintConfig() {
        return this.orgAndConfig?.config?.cardPrint;
    }

    get canFlipCard() {
        if (this.cardType !== CardType.LEGACY) {
            return [CardStatus.ACTIVE, CardStatus.EXPIRED].includes(this.status);
        }
        return this.subscriptions.length > 0;
    }

    get legacyCardShow() {
        return this.canFlipCard;
    }

    get isLegacyCard() {
        return this.card.cardType === CardType.LEGACY;
    }

    constructor(
        private translateService: TranslateService,
        private userService: UserService,
        private cardPrintService: CardPrintService,
        private shortDatePipe: ShortDatePipe,
        private store: Store<State>,
        private dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.store
            .select(organisationSelector)
            .pipe(
                filter((org) => !!org),
                take(1),
                tap((org) => (this.organisation = org)),
            )
            .subscribe();

        this.store
            .select(organisationTagSelector)
            .pipe(
                takeUntil(this.destroy$),
                filter((tag) => !!tag),
                switchMap((tag) => this.userService.getOrganisationFeatures(tag).pipe(takeUntil(this.destroy$))),
            )
            .subscribe((response) => {
                this.organisationFeatures = response;
            });

        this.store
            .select(organisationConfigurationSelector)
            .pipe(take(1))
            .subscribe((config) => {
                this.canShowSubscriptions = config?.adminTabs?.find((tab) => tab.tag === 'SUBSCRIPTION')?.visible;
            });

        this.userService.getAllCoreConfig().subscribe(
            (response) => {
                this.allCoreConfig = response;
            },
            (error) => {
                console.error(`Failed to fetch the all core config. Stacktrace: ${error}`);
            },
        );
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggleCardView() {
        if (this.cardView === CardView.DEFAULT) {
            this.cardView = CardView.SUBSCRIPTIONS;
        } else {
            this.cardView = CardView.DEFAULT;
        }
    }

    isExpired(endDate: string) {
        return endDate && new Date(endDate) < new Date();
    }

    getSubscriptionCode(sub: { code: string; inheritanceCode?: string }): string {
        return sub.inheritanceCode ? sub.inheritanceCode : sub.code;
    }

    getSubscriptionTypeText(type: SubscriptionType) {
        switch (type) {
            case SubscriptionType.FAMILY:
                return this.translateService.instant('pages.user-cards.subscription.type.family');
            default:
                return this.translateService.instant('pages.user-cards.subscription.type.individual');
        }
    }

    getToggleTooltip() {
        if (this.cardView === CardView.DEFAULT) {
            return this.translateService.instant('pages.user-cards.my-card.view-subscriptions');
        } else {
            return this.translateService.instant('pages.user-cards.my-card.view-card');
        }
    }

    getPrintTooltip() {
        return this.translateService.instant('pages.user-cards.common.button.print');
    }

    async printCardQRcode() {
        const cardPrintTemplate = this.orgAndConfig?.config?.cardPrint?.qrCode || this.allCoreConfig.defaultCardPrint.qrCode;

        const url = (
            await this.cardPrintService.generateQrCodePdf(
                this.card,
                this.organisation,
                this.orgAndConfig.config,
                this.subscriptions,
                this.translateService,
                cardPrintTemplate,
                false,
                this.shortDatePipe,
            )
        ).url;
        window.open(url.toString(), '_blank');
    }

    async printCardBarCode() {
        const cardPrintTemplate = this.orgAndConfig?.config?.cardPrint?.barCode || this.allCoreConfig.defaultCardPrint.barCode;

        const url = (
            await this.cardPrintService.generateBarCodePdf(
                this.card,
                this.organisation,
                this.orgAndConfig.config,
                this.subscriptions,
                this.translateService,
                cardPrintTemplate,
                false,
                this.shortDatePipe,
            )
        ).url;
        window.open(url.toString(), '_blank');
    }

    printCard() {
        this.cardPrintLoading = true;

        if (this.cardConfig.barcode === BardCodeType.QRCODE) {
            this.printCardQRcode();
        }

        if (this.cardConfig.barcode === BardCodeType.BARCODE) {
            this.printCardBarCode();
        }
        this.cardPrintLoading = false;
    }

    getBase64Image(img) {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        return dataURL;
    }

    isExpDateUpdatable(cardType: CardType) {
        if (this.status !== CardStatus.ACTIVE && this.status !== CardStatus.EXPIRED) {
            return false;
        }

        switch (cardType) {
            case CardType.BCITI:
                return this.organisationFeatures?.card?.BCITI?.canUpdate || false;
            case CardType.LEGACY:
                return this.organisationFeatures?.card?.LEGACY?.canUpdate || false;
            case CardType.CITIZEN:
                return this.organisationFeatures?.card?.CITIZEN?.canUpdate || false;
            case CardType.LIBRARY:
                return this.organisationFeatures?.card?.LIBRARY?.canUpdate || false;
            case CardType.SPORTS:
                return this.organisationFeatures?.card?.SPORTS?.canUpdate || false;
        }
    }

    getCardTypeClass(cardType: CardType) {
        switch (cardType) {
            case CardType.BCITI:
                return 'card-bciti';
            case CardType.LEGACY:
                return 'card-legacy';
            case CardType.CITIZEN:
                return 'card-citizen';
            case CardType.LIBRARY:
                return 'card-library';
            case CardType.SPORTS:
                return 'card-sports';
            default:
                return 'card-other';
        }
    }

    getSystExtLinkButtonClass(cardType: CardType) {
        switch (cardType) {
            case CardType.LIBRARY:
                return 'syst-ext-button-library';
            case CardType.SPORTS:
                return 'syst-ext-button-sports';
            default:
                return 'syst-ext-button-other';
        }
    }

    formatDate(
        date: string,
        options: Intl.DateTimeFormatOptions = {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        },
    ) {
        if (!date || this.status !== CardStatus.ACTIVE) {
            return '';
        }
        return `Exp: ${new Intl.DateTimeFormat(`${this.translateService.currentLang}-CA`, options).format(new Date(date))}`;
    }

    formatDateUTC(
        date: string,
        options: Intl.DateTimeFormatOptions = {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC',
        },
    ) {
        if (!date || this.status !== CardStatus.ACTIVE) {
            return '';
        }
        return `Exp: ${new Intl.DateTimeFormat(`${this.translateService.currentLang}-CA`, options).format(new Date(date))}`;
    }

    subscribeToCard() {
        this.cardActions.emit({
            action: CardActions.SUBSCRIBE,
            card: this.card,
        } as CardActionPayload);
    }

    renewCard() {
        this.cardActions.emit({
            action: CardActions.RENEW,
            card: this.card,
        } as CardActionPayload);
    }

    linkCard() {
        this.cardActions.emit({
            action: CardActions.LINK,
            card: this.card,
        } as CardActionPayload);
    }

    updateCardExpDate() {
        // A renewal in progress doesn't change status (it stays ACTIVE throughout) but the backend
        // rejects any edit while card.renewal is set — explain why instead of letting the request fail.
        if (!!this.card?.renewal) {
            const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
                width: '500px',
                panelClass: 'bciti-confirmation-dialog',
                data: {
                    title: this.translateService.instant('dialogs.cardSyncInProgress.title'),
                    body: this.translateService.instant('dialogs.cardSyncInProgress.body'),
                    buttons: {
                        confirm: {
                            text: this.translateService.instant('common.close'),
                            class: 'bciti-button bciti-button--blue',
                        },
                    },
                },
            });
            confirmDialog.componentInstance.onSubmit.pipe(takeUntil(confirmDialog.afterClosed())).subscribe(() => {
                confirmDialog.componentInstance.dialogRef.close();
            });
            return;
        }

        this.cardActions.emit({
            action: CardActions.UPDATEEXPDATE,
            card: this.card,
        } as CardActionPayload);
    }

    systExtLink() {
        this.userService
            .getSystExtLink(this.card?.cardType)
            .pipe(tap((systExtLink) => window.open(systExtLink, '_blank')))
            .subscribe();
    }
}
